import React, { useState } from "react";
import { ArrowRight, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { Reveal } from "@/components/Reveal";
import { submitNetlifyForm } from "@/lib/api";

const empty = { name: "", email: "", phone: "", vessel_length: "", message: "" };

function Field({ label, id, type = "text", value, onChange, testid, name }) {
  return (
    <label className="block group">
      <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate group-focus-within:text-cyan transition-colors">{label}</span>
      <input type={type} name={name} value={value} onChange={onChange} data-testid={testid}
        className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-cyan outline-none py-3 font-sans text-lg text-white placeholder:text-slate/40 transition-colors" />
    </label>
  );
}

export default function Contact() {
  const { t } = useApp();
  const [form, setForm] = useState(empty);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t.form.required); return; }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error(t.form.invalidEmail); return; }
    try {
      await submitNetlifyForm("contact", { ...form, source: "contact_form" });
      setForm(empty);
      toast.success(t.contact.success);
    } catch {
      toast.error("Network error — please try again");
    }
  };

  const details = [
    { Icon: Mail, label: t.contact.email_label, value: "info@marinenavelectronics.com" },
    { Icon: MapPin, label: t.contact.area_label, value: "Fort Lauderdale · Miami · Palm Beach" },
    { Icon: Clock, label: t.contact.hours_label, value: t.contact.hours },
  ];

  return (
    <section id="contact" className="relative bg-ink text-white overflow-hidden" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
        {/* header */}
        <Reveal>
          <div className="flex flex-col justify-between gap-6 border-b border-white/12 pb-10">
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] max-w-3xl">{t.contact.title}</h2>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 mt-14">
          {/* details */}
          <Reveal className="lg:col-span-4">
            <div className="divide-y divide-white/10">
              {details.map(({ Icon, label, value }) => (
                <div key={label} className="flex items-start gap-4 py-6 first:pt-0">
                  <Icon className="w-5 h-5 text-cyan mt-1 shrink-0" />
                  <div>
                    <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate mb-1.5">{label}</p>
                    <p className="font-sans text-lg text-white leading-snug break-words">{value}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 font-display text-2xl text-slate leading-snug">{t.footer.tag}</p>
          </Reveal>

          {/* form */}
          <Reveal delay={0.1} className="lg:col-span-8">
            <form
              onSubmit={submit}
              name="contact"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              className="grid sm:grid-cols-2 gap-x-10 gap-y-8"
              data-testid="contact-form"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p hidden><label>Don't fill: <input name="bot-field" /></label></p>
              <Field name="name" label={t.contact.name} id="name" value={form.name} onChange={set("name")} testid="contact-input-name" />
              <Field name="email" label={t.contact.email} id="email" type="email" value={form.email} onChange={set("email")} testid="contact-input-email" />
              <Field name="phone" label={t.contact.phone} id="phone" type="tel" value={form.phone} onChange={set("phone")} testid="contact-input-phone" />
              <Field name="vessel_length" label={t.contact.vessel} id="vessel_length" value={form.vessel_length} onChange={set("vessel_length")} testid="contact-input-vessel_length" />
              <label className="block sm:col-span-2 group">
                <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate group-focus-within:text-cyan transition-colors">{t.contact.message}</span>
                <textarea rows={3} name="message" value={form.message} onChange={set("message")} data-testid="contact-input-message"
                  className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-cyan outline-none py-3 font-sans text-lg text-white resize-none transition-colors" />
              </label>
              <div className="sm:col-span-2 flex justify-end pt-2">
                <button type="submit" data-testid="contact-submit"
                  className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-wider bg-cyan text-white pl-8 pr-6 py-4 rounded-sm hover:bg-cyan/90 transition-colors">
                  {t.contact.submit}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
